// Standalone strain-reviews — menu adapters.
//
// Customers embed our one <script> tag on their menu page. Most dispensary
// menus render product cards directly into the page DOM (Dutchie's embedded
// menu renders INLINE on the dispensary's own domain — confirmed on real
// Silverleaf: cards live at [data-testid="product-card-div"] with the title
// at [data-testid^="product-name-"], NOT inside a cross-origin iframe).
//
// This module auto-detects those cards, extracts the strain name from each
// product title, and injects our "See what people say" trigger button. A
// MutationObserver re-scans as the SPA renders more cards (category change,
// infinite scroll, search). Pages with no recognizable menu just no-op.

import { el } from './dom.js';


// --- strain-name extraction -------------------------------------------------

// Dutchie product titles look like:
//   "RUNTZ | 3.5G | FLOWER"   -> "RUNTZ"
//   "BN X TMZ | 3.5G"         -> "BN X TMZ"
//   "Blue Dream 3.5g"         -> "Blue Dream 3.5g" (no pipe; leave as-is)
// We take the first pipe-delimited segment and collapse whitespace. The
// backend's normalizer is forgiving (case/þunctuation-insensitive), so we
// don't over-clean here.
export function strainFromTitle(title) {
    if (!title || typeof title !== 'string') return '';
    const first = title.split('|')[0];
    return first.replace(/\s+/g, ' ').trim();
}


// --- onlyPaths (specific-URL) matching --------------------------------------

// Returns true if `pathname` matches ANY pattern. A pattern matches when it is:
//   - an exact match for the pathname, OR
//   - a trailing-'*' prefix (e.g. "/menu/*" matches "/menu/flower"), OR
//   - a substring of the pathname (e.g. "/products" matches "/store/products/x")
function _pathMatches(pathname, patterns) {
    if (!Array.isArray(patterns) || !patterns.length) return false;
    const path = pathname || '';
    for (const raw of patterns) {
        if (typeof raw !== 'string' || !raw) continue;
        if (raw === path) return true;                                   // exact
        if (raw.charAt(raw.length - 1) === '*') {                        // prefix
            const prefix = raw.slice(0, -1);
            if (path.indexOf(prefix) === 0) return true;
        }
        if (path.indexOf(raw) !== -1) return true;                       // substring
    }
    return false;
}


// We inject on every product EXCEPT clearly non-strain ones (edibles, drinks,
// accessories, apparel) where a strain popup makes no sense. Checked against
// the card's link href + the title text. This permissive default means flower
// / vape / pre-roll / concentrate all get a button without us having to
// enumerate every strain-bearing category name. Set injectAll to bypass.
const NON_STRAIN_RE =
    /(gumm|edible|chocolate|brownie|cookie|\bcandy\b|mint|drink|beverage|soda|root\s*beer|seltzer|lemonade|juice|tincture|topical|balm|lotion|salve|cream|battery|batter|accessor|apparel|merch|grinder|\bpaper\b|cone|lighter|tray|hat|shirt|sticker)/i;


// --- the adapter ------------------------------------------------------------

const ADAPTERS = [
    {
        // Dispense menu LISTING cards (Silverleaf et al.). Confirmed on the
        // real site: every card is [data-testid="product-card-div"] with the
        // title at [data-testid^="product-name-"].
        name: 'dispense-card',
        nameSelector: '[data-testid^="product-name-"]',
        cardSelector: '[data-testid="product-card-div"]',
        // Pull the title out of the name node's testid (most reliable) or its
        // text. testid form: "product-name-RUNTZ | 3.5G | FLOWER".
        titleFor(nameEl) {
            const tid = nameEl.getAttribute('data-testid') || '';
            const fromTid = tid.replace(/^product-name-/, '');
            return fromTid || (nameEl.textContent || '').trim();
        },
        hrefFor(nameEl, card) {
            const a = (card && card.querySelector('a[href]')) || nameEl.closest('a[href]');
            return a ? (a.getAttribute('href') || '') : '';
        },
    },
    {
        // Dispense PRODUCT DETAIL page. The title is the <h1> inside
        // [data-testid="product-page-product-info"]; there's no per-card link
        // so we use the page URL for the category check.
        name: 'dispense-pdp',
        nameSelector: '[data-testid="product-page-product-info"] h1',
        cardSelector: '[data-testid="product-page-product-info"]',
        titleFor(nameEl) {
            return (nameEl.textContent || '').trim();
        },
        hrefFor() {
            return (typeof location !== 'undefined' && location.pathname) || '';
        },
    },
];


// Neutral fallback shown until the strain's real source_count resolves — we
// NEVER render a wrong/placeholder number, so a {count}-templated label shows
// this instead until updateButtonCounts() fills the count in.
const FALLBACK_BTN_TEXT = 'See what online sources say';

// Resolve the initial label for a button. When the configured template
// contains the {count} placeholder and we don't yet know the count, render the
// neutral fallback. A template without {count} is used verbatim.
function _initialButtonText(template) {
    const t = (typeof template === 'string' && template) ? template : 'See what people say';
    if (t.indexOf('{count}') !== -1) return FALLBACK_BTN_TEXT;
    return t;
}

// Normalize a strain name to the cache/match key — lowercase, collapse any run
// of non-alphanumerics to a single underscore, strip leading/trailing ones.
// Mirrors the backend normalize_strain_name + the chatbot's _normStrainName so
// counts keyed by normalized name line up across the widget.
function _normName(s) {
    if (!s || typeof s !== 'string') return '';
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// sessionStorage key for a cached per-strain source count. Mirrors the
// chatbot's getCachedSourceCount pattern (strain_info.js) so the widgets share
// caching semantics (per-tab, survives SPA route changes).
const _SC_CACHE_PREFIX = 'vbsr_sc_';

function _readCachedCount(norm) {
    if (!norm) return null;
    try {
        if (typeof sessionStorage === 'undefined') return null;
        const v = sessionStorage.getItem(_SC_CACHE_PREFIX + norm);
        if (v == null) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
    } catch (_e) { return null; }
}

function _writeCachedCount(norm, n) {
    if (!norm || !Number.isFinite(n)) return;
    try {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.setItem(_SC_CACHE_PREFIX + norm, String(n));
    } catch (_e) { /* storage disabled */ }
}

// Render the count-aware label from a template. Singularizes "sources"->"source"
// when n === 1.
function _renderCountText(template, n) {
    let txt = template.replace('{count}', String(n));
    if (n === 1) txt = txt.replace(/\bsources\b/g, 'source');
    return txt;
}


function makeButton(strain, opts) {
    const btn = el('button', {
        class: 'vbsr-inject-btn',
        type: 'button',
        'data-strain-name': strain,
        'aria-label': 'See what people say about ' + strain,
    }, [
        el('span', { class: 'vbsr-inject-btn-text', text: _initialButtonText(opts.buttonText) }),
        el('span', { class: 'vbsr-inject-btn-arrow', text: '→' }),
    ]);
    // The button lives inside a clickable product card / link — stop the card's
    // own navigation from firing when our button is clicked. Capture phase so
    // we beat the card's handler. The actual open is handled by the global
    // [data-strain-name] click delegate in index.js, but we also stop
    // propagation here so the card link doesn't also navigate.
    btn.addEventListener('click', function (e) {
        e.stopPropagation();
        // Don't preventDefault here — index.js's delegate calls open(); we just
        // need to make sure the surrounding <a> doesn't navigate. stopPropagation
        // in capture handles bubbling; the delegate runs on document and still
        // sees the click because it's also registered... actually the delegate
        // is on document (bubble). stopPropagation would block it. So instead we
        // open directly here and preventDefault the card nav.
        e.preventDefault();
        if (window.VBStrainReviews && window.VBStrainReviews.open) {
            window.VBStrainReviews.open(strain);
        }
    }, true);
    return btn;
}


function injectInto(adapter, opts) {
    const nameNodes = document.querySelectorAll(adapter.nameSelector);
    let added = 0;
    nameNodes.forEach(function (nameEl) {
        const card = nameEl.closest(adapter.cardSelector) || nameEl.parentElement;
        if (!card) return;
        if (card.querySelector('.vbsr-inject-btn')) return;          // dedupe
        const title = adapter.titleFor(nameEl);
        const strain = strainFromTitle(title);
        if (!strain) return;
        if (!opts.injectAll) {
            const href = adapter.hrefFor(nameEl, card);
            const haystack = (title + ' ' + href);
            if (NON_STRAIN_RE.test(haystack)) return;                // skip edibles/drinks/accessories
        }
        const btn = makeButton(strain, opts);
        // Place directly under the product title.
        if (nameEl.parentElement) {
            nameEl.parentElement.insertBefore(btn, nameEl.nextSibling);
            added++;
        }
    });
    return added;
}


function injectStyles() {
    if (document.getElementById('vbsr-inject-styles')) return;
    const css = `
.vbsr-inject-btn {
    display: inline-flex; align-items: center; gap: 6px;
    margin: 6px 0 2px;
    background: var(--chatbot-primary-color, #3d7233);
    color: #fff; border: 0;
    padding: 6px 12px; border-radius: 999px;
    font: 600 11px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    cursor: pointer; width: fit-content;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
    transition: filter 0.12s, transform 0.1s;
}
.vbsr-inject-btn:hover { filter: brightness(1.08); transform: translateY(-1px); }
.vbsr-inject-btn-arrow { opacity: 0.75; }
`;
    const style = document.createElement('style');
    style.id = 'vbsr-inject-styles';
    style.textContent = css;
    document.head.appendChild(style);
}


/**
 * Scan the page for a known menu layout and inject trigger buttons. Safe to
 * call repeatedly (deduped per card). Returns total buttons added this pass.
 */
export function scanAndInject(opts) {
    opts = opts || {};
    // onlyPaths (specific-URL) mode: if configured, only inject on matching
    // pages. Checked here (not just at startup) because the MutationObserver
    // re-invokes scanAndInject and SPA navigation can change location.pathname.
    if (Array.isArray(opts.onlyPaths) && opts.onlyPaths.length && !_pathMatches(location.pathname, opts.onlyPaths)) return 0;
    injectStyles();
    let total = 0;
    for (const adapter of ADAPTERS) {
        if (document.querySelector(adapter.nameSelector)) {
            total += injectInto(adapter, opts);
        }
    }
    return total;
}


/**
 * Start auto-injection: one immediate pass + a debounced MutationObserver so
 * late-rendered cards (SPA navigation, infinite scroll) also get buttons.
 * Idempotent — repeated calls don't stack observers.
 */
export function startMenuAutoInject(opts) {
    opts = opts || {};
    if (window.__vbsrMenuObserver) {
        scanAndInject(opts);
        return;
    }
    scanAndInject(opts);
    let deb = null;
    const obs = new MutationObserver(function () {
        if (deb) clearTimeout(deb);
        deb = setTimeout(function () { scanAndInject(opts); }, 300);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    window.__vbsrMenuObserver = obs;
}


/**
 * Rewrite every injected button's visible label to the count-aware template
 * once the strain's real source_count is known.
 *
 * For each .vbsr-inject-btn on the page we read its data-strain-name, normalize
 * it (matching the backend normalize), look up the count in `countsByNormName`
 * (a { normalizedName: count } map), and rewrite .vbsr-inject-btn-text to
 * template.replace('{count}', n) — singularizing "sources"->"source" when n===1.
 * Buttons whose count is still unknown keep the neutral fallback label.
 *
 * The template is only applied when it contains the {count} placeholder; a
 * static template (no placeholder) is left untouched. Counts are cached per
 * normalized name in sessionStorage (vbsr_sc_<norm>) so labels stay correct
 * across SPA route changes / reloads without re-fetching.
 *
 * @param {Object<string, number>} countsByNormName  normalized-name -> count
 * @param {string} template  e.g. "Check what {count} sources say about this strain"
 */
export function updateButtonCounts(countsByNormName, template) {
    const tpl = (typeof template === 'string' && template) ? template : '';
    if (tpl.indexOf('{count}') === -1) return;  // static label — nothing to do
    const map = countsByNormName || {};
    const btns = document.querySelectorAll('.vbsr-inject-btn');
    btns.forEach(function (btn) {
        const name = btn.getAttribute('data-strain-name') || '';
        const norm = _normName(name);
        if (!norm) return;
        let n = map[norm];
        if (!Number.isFinite(n)) n = _readCachedCount(norm);
        if (!Number.isFinite(n) || n <= 0) return;  // keep fallback until known + > 0
        _writeCachedCount(norm, n);
        const textEl = btn.querySelector('.vbsr-inject-btn-text');
        if (textEl) textEl.textContent = _renderCountText(tpl, n);
    });
}

// Expose the normalizer + cache reader so index.js can collect unique strain
// names + decide which ones still need a network fetch (cache-first).
export { _normName as normStrainName, _readCachedCount as readCachedSourceCount };
